import { z } from "zod";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LoadScript, Autocomplete, GoogleMap, Marker } from "@react-google-maps/api";
import { twMerge } from "tailwind-merge";
import { makeFormField } from "./Form";
import { config } from "../config";

// --- your schema (reuse)
export const GoogleAddressZod = z.object({
  venue: z.string().optional(),
  fullAddress: z.string().optional(),
  coords: z.object({ lat: z.number(), long: z.number() }).optional(),
  timezone: z.string().optional(),
  placeId: z.string().optional(),
  formattedAddress: z.string().optional(),
  addressComponents: z
    .object({
      address1: z.string().optional(),
      address2: z.string().optional(),
      city: z.string().optional(),
      province: z.string().optional(),
      regionCode: z.string().optional(),
      country: z.string().optional()
    })
    .optional()
});
export type GoogleAddress = z.infer<typeof GoogleAddressZod>;

async function fetchTimezoneFromCoords(lat: number, lng: number, apiKey: string): Promise<string | undefined> {
  try {
    const timestamp = Math.floor(Date.now() / 1000);
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${apiKey}`
    );

    if (!res.ok) return undefined;
    const data = await res.json();
    if (data.status === "OK" && typeof data.timeZoneId === "string") {
      return data.timeZoneId;
    }
  } catch {
    // swallow – timezone is nice-to-have
  }
  return undefined;
}

function placeToGoogleAddress(place: google.maps.places.PlaceResult): GoogleAddress {
  const comps = place.address_components ?? [];
  const get = (type: string, short = false) => comps.find(c => c.types.includes(type))?.[short ? "short_name" : "long_name"];

  const streetNumber = get("street_number") ?? "";
  const route = get("route") ?? "";
  const address1 = (streetNumber + " " + route).trim() || undefined;

  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();

  const formatted = place.formatted_address ?? undefined;
  const firstAddressComponent = formatted?.split(",")?.[0]?.trim();
  const venue =
    (place.name && place.name !== formatted && place.name !== firstAddressComponent ? place.name : firstAddressComponent) ??
    undefined;

  return {
    venue,
    fullAddress: formatted,
    formattedAddress: formatted,
    placeId: place.place_id ?? undefined,
    coords: lat !== undefined && lng !== undefined ? { lat, long: lng } : undefined,
    addressComponents: {
      address1,
      address2: get("subpremise") ?? undefined,
      city: get("locality") || get("postal_town") || get("sublocality") || undefined,
      province: get("administrative_area_level_1") || undefined,
      regionCode: get("administrative_area_level_1", true) || undefined,
      country: get("country") || undefined
    }
  };
}

// ---- The raw input component (value/onChange/onBlur compatible with makeFormField)
export type GoogleAddressInputProps = {
  label?: string;
  value?: GoogleAddress;
  onChange?: (val: GoogleAddress | undefined) => void;
  onBlur?: () => void;
  placeholder?: string;
  disabled?: boolean;
  // makeFormField will pass `error` boolean if you want to style error states
  error?: boolean;
  className?: string;
};

export function GoogleAddressInput(p: GoogleAddressInputProps) {
  const apiKey = config.firebaseWebConfig.apiKey;
  const { label, value, onChange, onBlur, placeholder, disabled, error, className } = p;

  const acRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [center, setCenter] = useState<google.maps.LatLngLiteral | undefined>(
    value?.coords ? { lat: value.coords.lat, lng: value.coords.long } : undefined
  );

  // Sync center and input text when value changes externally (e.g. club field selection)
  useEffect(() => {
    if (value?.coords) {
      setCenter({ lat: value.coords.lat, lng: value.coords.long });
    } else {
      setCenter(undefined);
    }
    if (inputRef.current) {
      inputRef.current.value = value?.formattedAddress ?? "";
    }
  }, [value?.coords?.lat, value?.coords?.long, value?.formattedAddress]);

  const onPlaceChanged = useCallback(async () => {
    const place = acRef.current?.getPlace();
    if (!place) return;

    const parsed = placeToGoogleAddress(place);

    let timezone: string | undefined;
    if (parsed.coords) {
      timezone = await fetchTimezoneFromCoords(parsed.coords.lat, parsed.coords.long, apiKey);
    }

    const finalValue: GoogleAddress = {
      ...parsed,
      timezone: timezone ?? parsed.timezone
    };

    onChange?.(finalValue);
    if (finalValue.coords) {
      setCenter({ lat: finalValue.coords.lat, lng: finalValue.coords.long });
    }
  }, [apiKey, onChange]);

  const mapCenter = useMemo(() => center, [center]);

  return (
    <LoadScript googleMapsApiKey={apiKey} libraries={["places"]}>
      <div className={className} style={{ display: "grid", gap: 8 }}>
        {label ? <label className="text-sm font-semibold text-slate-700">{label}</label> : null}

        <Autocomplete
          onLoad={ac => (acRef.current = ac)}
          onPlaceChanged={onPlaceChanged}
          options={{ fields: ["address_components", "geometry", "formatted_address", "place_id", "name"] }}
        >
          <input
            ref={inputRef}
            placeholder={placeholder ?? "Start typing an address..."}
            disabled={disabled}
            onBlur={onBlur}
            className={twMerge(
              "h-10 w-full rounded-lg border px-3 outline-none",
              error ? "border-red-400" : "border-slate-300 focus:border-slate-400 bg-white"
            )}
            defaultValue={value?.formattedAddress ?? ""}
          />
        </Autocomplete>

        {value?.formattedAddress && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
            <div className="font-semibold">{value.formattedAddress}</div>
            <div className="opacity-80">
              {[value.addressComponents?.city, value.addressComponents?.province, value.addressComponents?.country]
                .filter(Boolean)
                .join(", ")}
            </div>
          </div>
        )}

        {mapCenter && (
          <div className="overflow-hidden rounded-xl border border-slate-200" style={{ height: 180 }}>
            <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={mapCenter} zoom={14} clickableIcons={false}>
              <Marker position={mapCenter} />
            </GoogleMap>
          </div>
        )}
      </div>
    </LoadScript>
  );
}

// ---- Wrap with your makeFormField so it plugs into <Form>
export const PrettyGoogleAddressInput = makeFormField<GoogleAddressInputProps, GoogleAddress | undefined>(GoogleAddressInput);
